<script setup lang="ts">
import { DatepickerComponent, DialogModal } from '@metapic-apps/shared'
import { type FormInstance, type FormRules } from 'element-plus'
import { reactive, ref } from 'vue'

import { useAddAdditionalPaymentRequest } from '@/api/payments.api'
import { notification } from '@/lib/helpers'
import { maxAmountRule, maxLengthRule, positiveAmountRule, requiredRule } from '@/lib/rules/common.rules'
import type { PaymentFormType } from '@/lib/types/payments.types'
import { BoxComponent, ButtonComponent, FlexComponent, InputComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  currency: string
  advertiserId: number
}>()
const emits = defineEmits(['handleSubmitSuccess'])
const { isLoading, error, addPayment } = useAddAdditionalPaymentRequest()
const isOpen = defineModel<boolean>('modelValue', { default: false })
const isDirty = ref(false)
const ruleFormRef = ref<FormInstance>()
const formData = reactive<PaymentFormType>({
  title: '',
  invoiceAmount: '',
  userEarnings: '',
  creatorId: '',
  paymentDate: '',
})
const rules = reactive<FormRules>({
  common: [
    {
      validator: (_, __, callback) => {
        if (!formData.userEarnings && !formData.invoiceAmount) {
          callback(new Error('Fill in "Invoice amount" or "Creator earnings" field'))
        } else {
          callback()
        }
      },
      trigger: 'blur',
    },
  ],
  paymentDate: [requiredRule],
  title: [requiredRule, maxLengthRule],
  creatorId: [
    positiveAmountRule,
    {
      validator: (_, value: string, callback) => {
        if ((formData.userEarnings as number) > 0 && !value) {
          callback(new Error('This field is required'))
        } else {
          callback()
        }
      },
      trigger: 'blur',
    },
  ],
  invoiceAmount: [maxAmountRule],
  userEarnings: [maxAmountRule],
})
const submitForm = async () => {
  await ruleFormRef.value?.validate(async (valid) => {
    if (valid) {
      await addPayment(formData, props.advertiserId)
      if (!error.value) {
        emits('handleSubmitSuccess')
        closeForm()
        notification.success('The additional payment was created successfully')
      }
    }
  })
}
const closeForm = () => {
  ruleFormRef.value?.resetFields()
  isOpen.value = false
}
</script>
<template>
  <DialogModal v-model="isOpen" @before-close="closeForm">
    <template #header>
      <BoxComponent :mb="24">
        <TypographyComponent variant="headline-medium">Add additional payment</TypographyComponent>
      </BoxComponent>
    </template>
    <el-form
      ref="ruleFormRef"
      :model="formData"
      :rules="rules"
      hide-required-asterisk
      :validate-on-rule-change="false"
      :scroll-to-error="true"
      label-position="top"
      @change="isDirty = true"
    >
      <FlexComponent direction="column" :gap="24">
        <el-form-item prop="validUntil">
          <DatepickerComponent
            v-model="formData.paymentDate"
            title="Payment date"
            placeholder="Select payment date"
            name="paymentDate"
            type="date"
            :disabled-date="() => false"
            :teleported="false"
          />
        </el-form-item>
        <el-form-item prop="title">
          <InputComponent v-model="formData.title" name="title">
            <template #header>
              <TypographyComponent variant="title-large">Title</TypographyComponent>
            </template>
          </InputComponent>
        </el-form-item>
        <el-form-item prop="invoiceAmount">
          <InputComponent v-model.number="formData.invoiceAmount" name="invoiceAmount" placeholder="0.00">
            <template #header>
              <TypographyComponent variant="title-large">Invoice amount</TypographyComponent>
            </template>
            <template #suffix>
              <TypographyComponent variant="title-large">
                {{ currency }}
              </TypographyComponent>
            </template>
          </InputComponent>
        </el-form-item>
        <FlexComponent :gap="16">
          <BoxComponent w="100%">
            <el-form-item prop="creatorId">
              <InputComponent v-model.integer="formData.creatorId" name="creatorId" placeholder="E.g. 75051">
                <template #header>
                  <TypographyComponent variant="title-large">Creator ID</TypographyComponent>
                </template>
              </InputComponent>
            </el-form-item>
          </BoxComponent>
          <BoxComponent w="100%">
            <el-form-item prop="userEarnings">
              <InputComponent v-model.number="formData.userEarnings" name="userEarnings" placeholder="0.00">
                <template #header>
                  <TypographyComponent variant="title-large">Creator earnings</TypographyComponent>
                </template>
                <template #suffix>
                  <TypographyComponent variant="title-large">
                    {{ currency }}
                  </TypographyComponent>
                </template>
              </InputComponent>
            </el-form-item>
          </BoxComponent>
        </FlexComponent>
        <div>
          <BoxComponent :h="16">
            <el-form-item prop="common" />
          </BoxComponent>
          <FlexComponent :gap="8" justify="flex-end">
            <ButtonComponent variant="text" :is-disabled="isLoading" @click="closeForm"> Cancel </ButtonComponent>
            <ButtonComponent variant="primary" :is-loading="isLoading" @click.prevent="submitForm"> Save </ButtonComponent>
          </FlexComponent>
        </div>
      </FlexComponent>
    </el-form>
  </DialogModal>
</template>
<style lang="scss" scoped></style>
